/**
 * The bookkeeping behind annotating elements inside an HTML artifact preview.
 *
 * Two surfaces point at one set of annotations: the preview iframe, where they
 * are written and where each leaves a numbered pin, and the dialog's tray,
 * which lists them in the order the agent will read them. Keeping those in step
 * comes down to a few rules — an edit replaces rather than appends, a second
 * pass over the same element replaces rather than duplicates, and only a
 * genuinely new target counts against the cap.
 *
 * They live here rather than inside `media-preview-dialog.tsx` because that
 * module cannot be imported under `tsx --test` (it pulls in `streamdown`, whose
 * package exports have no Node-resolvable main), and these rules are exactly
 * the part worth testing directly.
 */

import type { ArtifactElementQuoteSnapshot } from '@/lib/chat-types'

/**
 * One annotated element, paired with the id the preview iframe knows it by.
 *
 * The id keeps the two surfaces pointing at the same annotation — removing a
 * card has to take its pin with it, and editing a pin has to land on the card
 * it already made. It never leaves the browser; what gets sent is the snapshot.
 */
export interface SelectedArtifactElement {
  qid: string
  snapshot: ArtifactElementQuoteSnapshot
}

/**
 * A stable identity for an element, independent of the preview's own ids.
 *
 * Needed because those ids do not survive a preview reload: the injected script
 * starts numbering again, so without a content-derived key the same element
 * would come back as a second, separate target.
 */
export function artifactElementQuoteKey(
  element: ArtifactElementQuoteSnapshot,
): string {
  return [
    element.dom_path,
    element.selector_hints[0] ?? '',
    element.text_hash ?? '',
    element.outer_html_hash ?? '',
  ].join('|')
}

export interface ArtifactElementUpsertResult {
  elements: SelectedArtifactElement[]
  /** The list was already full and this was a new target, so nothing changed. */
  rejected: boolean
}

/**
 * Fold one annotation into the list, whether it is new or an edit.
 *
 * An edit must update in place: two rows for one element would hand the agent
 * two contradictory instructions for the same edit. The cap applies only to new
 * targets, so rewording the last annotation never trips it.
 */
export function upsertSelectedArtifactElement(
  current: readonly SelectedArtifactElement[],
  entry: SelectedArtifactElement,
  maxCount: number,
): ArtifactElementUpsertResult {
  const key = artifactElementQuoteKey(entry.snapshot)
  const at = current.findIndex(
    item =>
      item.qid === entry.qid || artifactElementQuoteKey(item.snapshot) === key,
  )
  if (at >= 0) {
    const elements = current.slice()
    elements[at] = entry
    return { elements, rejected: false }
  }
  if (current.length >= maxCount) {
    return { elements: [...current], rejected: true }
  }
  return { elements: [...current, entry], rejected: false }
}
